/**
 * Thin API client for the Qoryn backend.
 */
import { getAccessToken } from "@/lib/supabaseClient";

const BASE = process.env.REACT_APP_BACKEND_URL;
if (!BASE) {
    console.warn("REACT_APP_BACKEND_URL is not set. API calls will fail.");
}

export const API = `${BASE}/api`;

// Small helper: attach Bearer token if the user is signed in.
// Auth-required endpoints will 401 without it; public endpoints ignore it.
async function authHeader() {
    const tok = await getAccessToken();
    return tok ? { Authorization: `Bearer ${tok}` } : {};
}

export async function fetchHealth() {
    const r = await fetch(`${API}/health`);
    if (!r.ok) throw new Error(`Health check failed: ${r.status}`);
    return r.json();
}

export async function fetchVoices() {
    const r = await fetch(`${API}/realtime/voices`);
    if (!r.ok) throw new Error(`Voices fetch failed: ${r.status}`);
    return r.json();
}

export async function fetchGlossary() {
    const r = await fetch(`${API}/transcript/glossary`);
    if (!r.ok) throw new Error(`Glossary fetch failed: ${r.status}`);
    return r.json();
}

export async function fetchCapabilities() {
    const r = await fetch(`${API}/capabilities`);
    if (!r.ok) throw new Error(`Capabilities fetch failed: ${r.status}`);
    return r.json();
}

export async function normalizeRemote(text) {
    const r = await fetch(`${API}/transcript/normalize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
    });
    if (!r.ok) throw new Error(`Normalize failed: ${r.status}`);
    return r.json();
}

export async function mintClientSecret({ voice } = {}) {
    const r = await fetch(`${API}/realtime/client-secret`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            ...(await authHeader()),
        },
        body: JSON.stringify({ voice: voice ?? null }),
    });
    if (!r.ok) {
        let detail;
        const raw = await r.text(); try { detail = JSON.parse(raw); } catch { detail = { message: raw }; }
        const err = new Error(`client-secret request failed (${r.status})`);
        err.status = r.status;
        err.detail = detail;
        throw err;
    }
    return r.json();
}

// ---------------------------------------------------------------------------
// Session persistence (Phase 1C — Founder Console wiring)
//
// These helpers talk to /api/sessions and are consumed by
// useRealtimeSession to mirror the live voice conversation to the DB
// without ever blocking the WebRTC audio loop. All calls require the
// user to be signed in; they will 401 otherwise (the hook treats 401
// as "not persisted" and stops trying — no fabricated success).
// ---------------------------------------------------------------------------

/** POST /api/sessions — start a persistent DB row for the live session. */
export async function createBackendSession({
    project_id = null,
    voice = null,
    model = null,
    title = null,
} = {}) {
    const r = await fetch(`${API}/sessions`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            ...(await authHeader()),
        },
        body: JSON.stringify({ project_id, voice, model, title }),
    });
    if (!r.ok) {
        const err = new Error(`sessions.create failed (${r.status})`);
        err.status = r.status;
        try { err.detail = await r.json(); } catch { /* noop */ }
        throw err;
    }
    return r.json();
}

/**
 * PUT /api/sessions/{id}/turns/{turn_index} — idempotent upsert of a
 * single transcript turn. Used by the live console to persist SDK
 * transcript rows that GROW across their lifetime (in-progress → final)
 * without inserting duplicate rows.
 */
export async function upsertSessionTurn(
    sessionId,
    turnIndex,
    { speaker, raw_text = null, normalized_text = null, metadata = null } = {},
) {
    const r = await fetch(
        `${API}/sessions/${sessionId}/turns/${turnIndex}`,
        {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                ...(await authHeader()),
            },
            body: JSON.stringify({
                speaker,
                raw_text,
                normalized_text,
                metadata,
            }),
        },
    );
    if (!r.ok) {
        const err = new Error(`turns.upsert failed (${r.status})`);
        err.status = r.status;
        try { err.detail = await r.json(); } catch { /* noop */ }
        throw err;
    }
    return r.json();
}

/**
 * PATCH /api/sessions/{id} — update lifecycle fields. Marking status
 * to "ended" on the backend auto-enqueues session.summarize.v1.
 */
export async function patchBackendSession(sessionId, patch) {
    const r = await fetch(`${API}/sessions/${sessionId}`, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            ...(await authHeader()),
        },
        body: JSON.stringify(patch),
    });
    if (!r.ok) {
        const err = new Error(`sessions.patch failed (${r.status})`);
        err.status = r.status;
        try { err.detail = await r.json(); } catch { /* noop */ }
        throw err;
    }
    return r.json();
}

// ---------------------------------------------------------------------------
// Historical (supersession-lineage) retrieval — Phase 1D closure extension
//
// Backs the get_supersession_lineage realtime tool. Called on demand from
// within a live session when Qoryn detects a historical/lineage question.
// The endpoint is owner-scoped and returns the full multi-hop lineage for
// any decision text matching the query — CURRENT head plus every
// superseded ancestor/descendant, with provenance and superseded_by /
// superseded_at markers preserved.
//
// NOT injected at session bootstrap — that pipeline stays byte-identical.
// ---------------------------------------------------------------------------
export async function fetchSupersessionLineage({
    query,
    project_id = null,
    max_hops = 10,
} = {}) {
    const params = new URLSearchParams({ query });
    if (project_id) params.set("project_id", project_id);
    if (max_hops) params.set("max_hops", String(max_hops));
    const r = await fetch(`${API}/context/history?${params.toString()}`, {
        method: "GET",
        headers: { ...(await authHeader()) },
    });
    if (!r.ok) {
        const err = new Error(`context.history failed (${r.status})`);
        err.status = r.status;
        try { err.detail = await r.json(); } catch { /* noop */ }
        throw err;
    }
    return r.json();
}
